/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineUserGroup, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { Avatar } from "../../ui";
import { useTeamContext } from "../../context/TeamContext";
import { pushOneTeam } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type TeamMember = {
  team_id: number | string;
  firstname?: string;
  lastname?: string;
  role?: string;
  email?: string;
  thumbnail?: string;
  [key: string]: unknown;
};

const addRoute = "/team/add";
const editRoute = "/team/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { team, count, fetchTeams, deleteTeam, updateTeamState } =
    useTeamContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!team) fetchTeams(page, pageSize);
  }, [team]);

  const rows = useMemo<TeamMember[] | null>(() => {
    if (!Array.isArray(team)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return team as TeamMember[];
    return (team as TeamMember[]).filter(
      (t) =>
        String(t.firstname ?? "").toLowerCase().includes(q) ||
        String(t.lastname ?? "").toLowerCase().includes(q) ||
        String(t.role ?? "").toLowerCase().includes(q),
    );
  }, [team, query]);

  const columns: Column<TeamMember>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={`${row.firstname ?? ""} ${row.lastname ?? ""}`}
              className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-200"
            />
          ) : (
            <Avatar firstName={row.firstname} lastName={row.lastname} size="sm" />
          )}
          <div>
            <p className="font-medium text-gray-900">
              {[row.firstname, row.lastname].filter(Boolean).join(" ") || "—"}
            </p>
            {row.email ? (
              <p className="truncate text-[11.5px] text-gray-500">{row.email}</p>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      header: "Role",
      cell: (row) => (
        <span className="text-gray-600">{row.role ?? "—"}</span>
      ),
    },
  ];

  const handleDelete = async (row: TeamMember) => {
    const response: any = await deleteTeam(row.team_id);
    if (response?.status === 200 && Array.isArray(team)) {
      updateTeamState(
        (team as TeamMember[]).filter((t) => t.team_id !== row.team_id),
      );
    }
  };

  const rowActions = (row: TeamMember): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneTeam(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Remove ${row.firstname ?? "this team member"}?`,
        description: "They will no longer appear on the public team page.",
        confirmLabel: "Remove",
      },
    },
  ];

  return (
    <ListPage<TeamMember>
      title="Team"
      subtitle="Team members shown on the public about page."
      icon={<HiOutlineUserGroup className="h-5 w-5" />}
      actions={[{ label: "Add team member", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.team_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search by name or role…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchTeams(p, ps);
        },
      }}
      empty="No team members yet."
    />
  );
};

export default Overview;
